import type { PlanV2IssueCardPayload, PlanV2IssueQueue } from './plan-v2-issues';
import { mutedCheckKey } from './review-check-memory';
import { siteWideIssueKey } from './site-wide-suppression';

/**
 * What a page's ticks mean for the next page.
 *
 * Sending a page answers two different things at once, and keeping them
 * straight is the whole job here. The cards say what work this page needs. The
 * ticks inside them also say something about the CHECKS — that the operator has
 * seen this kind of finding and made up their mind — and that second answer is
 * what the remaining forty-four pages read.
 *
 * Three outcomes, and the third is the one that took a rewrite to get right:
 *
 * - **Ticked, site-wide** → settled. One template fix clears the finding on
 *   every page that has it, so asking again is asking for the same work twice.
 * - **Ticked, page-scoped** → nothing settled, and any earlier mute is cleared.
 *   Ticking it here is the operator changing their mind, and the next page has
 *   to show that.
 * - **Unticked, either scope** → muted. The check keeps appearing, unticked.
 *
 * The old rule answered every site-wide code with the PAGE's verdict: send
 * anything on the page and six template defects were queued; put the page aside
 * and the same six were settled as "no". Both were decisions the operator never
 * made, and the second was unreachable — once settled, the check never appeared
 * again for them to reconsider.
 */

export interface ReviewCheckState {
  code: string;
  siteWide: boolean;
  ticked: boolean;
}

export interface ReviewCheckWrite {
  issueKey: string;
  queue: PlanV2IssueQueue;
  payload?: PlanV2IssueCardPayload;
}

export interface ReviewCheckWritePlan {
  /** Sent to the batch endpoint alongside the page's own cards. */
  writes: ReviewCheckWrite[];
  /**
   * What to send to put the memory back exactly as it was.
   *
   * Not simply "delete what we wrote": a mute set two pages ago is not this
   * send's to clear, so a write that OVERWROTE one has to carry the old row
   * back rather than removing it.
   */
  undo: ReviewCheckWrite[];
}

export interface ReviewCheckWriteContext {
  urlId: string;
  url: string;
  /** Codes already muted, as written. */
  muted: ReadonlySet<string>;
  /** Codes already settled by a send, as written. */
  settled: ReadonlySet<string>;
}

function mutePayload(code: string, ctx: ReviewCheckWriteContext): PlanV2IssueCardPayload {
  return {
    issueKey: mutedCheckKey(code),
    urlId: ctx.urlId,
    url: ctx.url,
    issueType: 'check_muted',
    cardTitle: 'Check muted',
    changeSummary: `Not fixing: ${code}. It keeps appearing, unticked, until you tick it.`,
    why: `Unticked while reviewing ${ctx.url || 'this page'}.`
  };
}

function settledPayload(code: string, ctx: ReviewCheckWriteContext): PlanV2IssueCardPayload {
  return {
    issueKey: siteWideIssueKey(code),
    urlId: ctx.urlId,
    url: ctx.url,
    issueType: 'site_wide_technical',
    cardTitle: 'Site-wide technical',
    changeSummary: `Template defect: ${code}. Fixing it clears this finding on every page that has it.`,
    why: `Sent while reviewing ${ctx.url || 'this page'}.`
  };
}

export function reviewCheckWritePlan(
  checks: readonly ReviewCheckState[] | null | undefined,
  ctx: ReviewCheckWriteContext
): ReviewCheckWritePlan {
  const writes: ReviewCheckWrite[] = [];
  const undo: ReviewCheckWrite[] = [];

  for (const check of checks ?? []) {
    const code = (check?.code || '').trim();
    if (!code) continue;

    const muteKey = mutedCheckKey(code);
    const wasMuted = ctx.muted.has(code);

    if (!check.ticked) {
      // Already muted: nothing to say, and re-writing it would give undo a row
      // to remove that this send did not create.
      if (wasMuted) continue;
      writes.push({ issueKey: muteKey, queue: 'dismissed', payload: mutePayload(code, ctx) });
      undo.push({ issueKey: muteKey, queue: 'issues' });
      continue;
    }

    if (wasMuted) {
      writes.push({ issueKey: muteKey, queue: 'issues' });
      undo.push({ issueKey: muteKey, queue: 'dismissed', payload: mutePayload(code, ctx) });
    }

    if (!check.siteWide) continue;
    if (ctx.settled.has(code)) continue;

    const settledKey = siteWideIssueKey(code);
    writes.push({ issueKey: settledKey, queue: 'work_item', payload: settledPayload(code, ctx) });
    undo.push({ issueKey: settledKey, queue: 'issues' });
  }

  return { writes, undo };
}
